<template>
  <div class="row" v-if="!!workOrder">
    <div class="col-sm-3">
      <h1>{{ workOrder.orderNumber }}</h1>
    </div>

    <div class="col-sm-6 d-none d-lg-block" v-if="workOrder.canComplete"><h3>Podpis stranke</h3></div>
    <div class="col-sm-6 d-none d-lg-block" v-if="workOrder.status === 6"><h3>Ponovno pošiljanje</h3></div>

    <div class="col-sm-3">
      <h2><OrderBadge :workOrder="workOrder" /></h2>
    </div>
  </div>

  <div class="row mb-3" v-if="workOrder?.canComplete || workOrder?.status === 6">
    <div class="col-md-6" v-show="facility?.contacts?.length > 0">
      <h5>Pošlji podpisan nalog po emailu na:</h5>
      <div class="row mb-3" v-for="contact in facility?.contacts">
        <div class="col-sm-9">
          <div class="form-check">
            <input
              class="form-check-input"
              :class="{ 'is-invalid': v.invalid('signatureName') }"
              type="checkbox"
              :id="contact.id"
              v-model="contact.checked"
            />
            <label class="form-check-label" :for="contact.id"> {{ contact.name }} [{{ contact.email }}] </label>
          </div>
        </div>
        <div class="col-sm-3 text-end">
          <button
            class="btn btn-sm btn-danger"
            v-show="!contact.checked"
            @click="localDeleteFacilityContactAsync(contact)"
          >
            Zbriši
          </button>
        </div>
      </div>
    </div>
    <FacilityContactEdit
      class="col-md-6 bg-light rounded"
      v-model="facilityContact"
      @save="localSaveFacilityContactAsync(facilityContact)"
    />
  </div>

  <div class="row" v-show="workOrder?.canComplete">
    <div class="shadow-lg p-3 mb-5 bg-white rounded">
      <SignaturePad ref="signaturePadEl" :width="640" :height="400" style="max-width: 100%" />
    </div>
  </div>

  <RequiredWarning v-show="v.showWarning.value" :missing="v.missing.value" />

  <div class="row sticky-actions py-2 bg-light mb-5">
    <div class="col-2" v-show="workOrder?.canComplete">
      <button class="btn btn-outline-danger btn-lg me-3" type="button" @click="clear">Izbriši podpis</button>
    </div>
    <div class="col-10 text-end">
      <button
        class="btn btn-outline-secondary me-3"
        type="button"
        @click="$router.push({ name: 'ServiceOrder', params: { workOrderId: workOrder.id } })"
      >
        Prekliči
      </button>
      <button
        class="btn btn-primary btn-lg me-3"
        type="button"
        v-show="workOrder?.canComplete"
        :disabled="!worker.isSetupComplete.value"
        @click="signAsync"
      >
        Podpiši in zaključi nalog
      </button>
      <button
        class="btn btn-primary btn-lg me-3"
        type="button"
        v-show="workOrder?.status === 6"
        @click="resendAsync"
      >
        Pošlji nalog
      </button>
    </div>
  </div>
</template>

<script setup>
// source: https://github.com/jekorx/vue-canvas-sign
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { useAuth } from "../composition-api/useAuth"
import { useWorker } from "../composition-api/useWorker"
import { useWorkOrder } from "../composition-api/useWorkOrder"
import { setError, startLoading, stopLoading, setSuccess, clearNotifications } from "../components/Notification"
import { getFacilityAsync, saveFacilityContactAsync, deleteFacilityContactAsync } from "../api"
import { useValidation, hasText } from "../composition-api/useValidation"
import FacilityContactEdit from "../components/FacilityContactEdit.vue"
import SignaturePad from "../components/SignaturePad.vue"

defineOptions({ name: "SignOrder" })

const { worker } = useWorker()
const route = VueRouter.useRoute()
const router = VueRouter.useRouter()
const { fetchAuthAsync } = useAuth()
const { isNew, workOrder, sendEmailAsync } = useWorkOrder("Service")

const facilityContact = ref({})

const signatureName = computed(() => facility.value?.contacts?.find((c) => !!c.checked)?.name)

const v = useValidation({
  signatureName: [() => hasText(signatureName.value, 4), "Kontakt za podpis"],
})

const localSaveFacilityContactAsync = async (contact) => {
  await saveFacilityContactAsync(fetchAuthAsync, contact, facility.value)
  facilityContact.value = {}
  await loadFacilityAsync()
}

const localDeleteFacilityContactAsync = async (contact) => {
  if (await deleteFacilityContactAsync(fetchAuthAsync, contact)) {
    await loadFacilityAsync()
    setSuccess({ message: "Kontakt zbrisan" })
  }
}

const resendAsync = () => {
  if (!v.attempt()) return
  return sendEmailAsync("resendCompleted", { facility: facility.value })
}

const signAsync = async () => {
  if (!v.attempt()) return
  v.fromServer(null)
  startLoading("signAsync")
  try {
    const SignatureImageBase64 = signaturePadEl.value?.getBase64()
    const payload = {
      Type: "SignServiceOrder",
      ServiceOrderId: workOrder.value.id,
      SignatureName: signatureName.value,
      SendToFacilityContacts: facility.value?.contacts?.filter((c) => !!c.checked).map((c) => c.id),
      SignatureImageBase64,
      WarehouseId: worker.activeWarehouse.value?.id,
    }

    const raw = await fetchAuthAsync(`${AZURE_URI}/ServiceOrder/Command`, "POST", payload)
    if (raw?.status !== "ok") {
      throw raw
    }
    setSuccess({ message: 'Podpisano, status spremenjen v "Zaključeno"' })
    // the worker leaves the facility next: ask where to
    router.push({ name: "NextDestination" })
  } catch (e) {
    v.fromServer(e)
    setError(e)
  }
  stopLoading("signAsync")
}

const loadFacilityAsync = async () => {
  // only the contacts are needed here
  facility.value = await getFacilityAsync(fetchAuthAsync, workOrder.value.facilityID, true, false)
}

const signaturePadEl = ref(null)
const facility = ref(null)

const clear = () => {
  signaturePadEl.value?.clear()
}

/* TODO: candidate for de-duplication */
/* difference in populateDetails !! */
watch(
  workOrder,
  async () => {
    if (workOrder.value.facilityID !== facility.value?.id) {
      await loadFacilityAsync()
    }
  },
  { deep: true },
)
</script>
